/**
 * A conferência das etapas da raia eletrônica (E147, GUS-180, ADR-0101). Espelha
 * `apps/api/types/conferencia_das_etapas.py` e `apps/api/types/pedidos_das_etapas.py`.
 *
 * - `GET /casos/:id/etapas`: as oito etapas na ordem da tela, cada uma com a conferência pelo estado
 *   de hoje (ou a etapa aguardando, com o motivo), a última gravada, se a gravada vale e as métricas.
 *   Só a equipe: o banco recebe 403 e vê só o estado no caso.
 * - `POST /casos/:id/etapas/pedidos`: a equipe resolve a consulta do sistema que trava a análise —
 *   uma tentativa a mais (verifica o caso na hora) ou a limitação registrada com justificativa. A
 *   resposta é o pedido gravado; a tela relê as etapas depois.
 *
 * Nada é decidido aqui: a liberação, o motivo e a condição de saída chegam prontos do servidor. A
 * tela só escolhe a ação principal pela etapa que não liberou.
 */
import axios, { type AxiosInstance } from 'axios';

export type EtapaDoFluxo =
  | 'recebimento'
  | 'extracao'
  | 'analise'
  | 'resultado'
  | 'revisao'
  | 'composicao'
  | 'assinatura'
  | 'entrega';

export type ResultadoDoCriterio = 'passou' | 'falhou' | 'inconclusivo' | 'nao_se_aplica';
export type LiberacaoDaEtapa = 'aprovado' | 'corrigir' | 'bloqueado';
export type LiberacaoDoObjeto = 'aprovado' | 'corrigir' | 'bloqueado' | 'aguardando';
export type LiberacaoNoCaso = 'pendente' | 'aprovado' | 'corrigir' | 'bloqueado';
export type MotivoDeAguardar = 'etapa_anterior' | 'nao_executada';
export type DocumentoDaComposicao =
  | 'exame_tecnico_digital'
  | 'laudo_eletronico'
  // E171.F1.C02 (GUS-145): a prévia do relatório de risco também entra na composição.
  | 'relatorio_de_risco';

export type CriterioDaEtapa =
  | 'custodia_do_material'
  | 'recebimentos_selados'
  | 'leitura_conferida'
  | 'pecas_verificadas'
  | 'entradas_de_hoje'
  | 'regua_vigente'
  | 'verificacoes_alinhadas'
  | 'material_preservado'
  | 'consultas_do_sistema'
  | 'avaliacao_de_hoje'
  | 'veracidade_aprovada'
  | 'conclusao_proposta'
  | 'conclusao_do_perito'
  | 'conclusao_sobre_a_analise_vigente'
  | 'composta_da_etapa_vigente'
  | 'assinatura_registrada'
  | 'assinatura_com_certificado'
  | 'emissao_confere'
  | 'assinada_sobre_a_revisao_vigente'
  | 'entregue_com_assinatura'
  | 'aviso_da_entrega';

/** A condição de saída da etapa que não liberou (`CONDICAO_DE_SAIDA` do servidor). */
export type CondicaoDeSaida =
  | 'avaliar_de_novo'
  | 'corrigir_a_avaliacao'
  | 'revisar_na_mesa'
  | 'resolver_a_ocorrencia'
  | 'selar_o_recebimento'
  | 'resolver_a_pendencia_da_leitura'
  | 'verificar_as_pecas'
  | 'verificar_de_novo'
  | 'tentar_de_novo_ou_registrar_limitacao'
  | 'concluir_na_mesa'
  | 'reabrir_e_concluir'
  | 'emitir_de_novo'
  | 'assinar'
  | 'assinar_com_certificado'
  | 'conferir_o_arquivo_assinado'
  | 'conferir_a_revisao'
  | 'conferir_a_assinatura'
  | 'conferir_a_entrega';

/** O fundamento de cada critério, em chave, como a régua do servidor o escreve. */
export type FundamentoDoCriterio =
  | 'avaliacao_sobre_as_entradas_de_hoje'
  | 'entradas_mudaram_desde_a_avaliacao'
  | 'avaliacao_sem_impressao_do_fluxo'
  | 'veracidade_aprovada'
  | 'veracidade_a_corrigir'
  | 'veracidade_bloqueada'
  | 'conclusao_definida'
  | 'revisao_do_perito_pendente'
  | 'material_conferido'
  | 'ocorrencia_aberta'
  | 'recebimentos_selados'
  | 'selado_na_emissao'
  | 'leitura_aprovada'
  | 'leitura_em_retomada'
  | 'leitura_bloqueada'
  | 'conferencia_pendente'
  | 'caso_sem_ficha'
  | 'pecas_verificadas'
  | 'peca_sem_verificacao'
  | 'pecas_em_dia'
  | 'pecas_concluidas'
  | 'entradas_mudaram'
  | 'regua_anterior'
  | 'desalinhada'
  | 'material_preservado'
  | 'peca_parada_pela_custodia'
  | 'retirada_do_exame'
  | 'consultas_respondidas'
  | 'consulta_a_retomar'
  | 'orcamento_esgotado'
  | 'concluida_pelo_perito'
  | 'aguardando_o_perito'
  | 'apurada_pelo_motor'
  | 'aguardando_a_verificacao'
  | 'sobre_a_verificacao_lida'
  | 'verificada_depois_da_conclusao'
  | 'composta_da_etapa_vigente'
  | 'composta_de_rodada_anterior'
  | 'assinada'
  | 'assinatura_com_certificado'
  | 'assinatura_sem_certificado'
  | 'emissao_final_confere'
  | 'emissao_final_diverge'
  | 'revisao_vigente'
  | 'revisao_mudou_depois_da_assinatura'
  | 'assinatura_anterior_a_conferencia'
  | 'assinatura_sem_composicao_registrada'
  | 'entregue_com_assinatura'
  | 'entregue_sem_assinatura_aprovada'
  | 'aviso_derivado_da_entrega';

export type ProvedorExterno = 'lemit' | 'geocoder' | 'ip_intel' | 'mapa' | 'portal'; // branding:allow — chave do contrato do servidor, não exibida
export type AcaoDoPedido = 'tentativa' | 'limitacao';

export interface ProcedimentoData {
  nome: string;
  versao: string;
}

export interface CriterioConferidoData {
  chave: CriterioDaEtapa;
  resultado: ResultadoDoCriterio;
  critico: boolean;
  fundamento: string;
  parametros: Record<string, number | string>;
  objeto_id: string | null;
  se_falhar: 'corrigir' | 'bloqueado';
}

export interface ObjetoDaEtapaData {
  objeto_id: string;
  liberacao: LiberacaoDoObjeto;
}

export interface DocumentoCompostoData {
  documento: DocumentoDaComposicao;
  objeto_id: string | null;
  entrada_sha256: string;
  sha256: string;
}

export interface ConferenciaDaEtapaData {
  etapa: EtapaDoFluxo;
  versao_da_regra: number;
  executor: ProcedimentoData;
  auditor: ProcedimentoData;
  entrada_sha256: string;
  saida_sha256: string;
  criterios: CriterioConferidoData[];
  liberacao: LiberacaoDaEtapa;
  motivo: CriterioDaEtapa | null;
  condicao_de_saida: string | null;
  objetos: ObjetoDaEtapaData[];
  documentos: DocumentoCompostoData[];
}

export interface EtapaAguardandoData {
  etapa: EtapaDoFluxo;
  motivo: MotivoDeAguardar;
  depende_de: EtapaDoFluxo | null;
}

export interface ConferenciaDaEtapaRecordData {
  id: string;
  case_id: string;
  etapa: EtapaDoFluxo;
  versao: number;
  sha256: string;
  criada_em: string;
  conferencia: ConferenciaDaEtapaData;
}

export interface MetricasDaEtapaData {
  versoes: number;
  tentativas: number;
  limitacoes: number;
  primeira_conferencia_em: string | null;
  aprovada_em: string | null;
  segundos_ate_a_aprovacao: number | null;
}

export interface EtapaDoCasoData {
  etapa: EtapaDoFluxo;
  agora: ConferenciaDaEtapaData | null;
  aguardando: EtapaAguardandoData | null;
  gravada: ConferenciaDaEtapaRecordData | null;
  vigente: boolean;
  metricas: MetricasDaEtapaData;
}

export interface EtapasDoCasoData {
  case_id: string;
  etapas: EtapaDoCasoData[];
  conferida: boolean;
}

export interface PedidoDaEtapaData {
  acao: AcaoDoPedido;
  provedor: ProvedorExterno;
  justificativa?: string | null;
}

export interface PedidoDaEtapaRecordData {
  id: string;
  case_id: string;
  etapa: 'analise';
  acao: AcaoDoPedido;
  provedor: ProvedorExterno;
  justificativa: string | null;
  criada_por: string | null;
  criada_em: string;
}

export const JUSTIFICATIVA_MINIMA = 15;
export const JUSTIFICATIVA_MAXIMA = 500;

export type MotivoDaFalhaDasEtapas = 'sem_permissao' | 'indisponivel';

export class EtapasDoCasoError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDasEtapas) {
    super(motivo);
    this.name = 'EtapasDoCasoError';
  }
}

/** Por que o pedido da equipe não foi atendido, pelo código do servidor. */
export type RecusaDoPedidoDaEtapa = 'sem_falha' | 'justificativa' | 'indisponivel';

export class PedidoDaEtapaError extends Error {
  constructor(readonly recusa: RecusaDoPedidoDaEtapa) {
    super(recusa);
    this.name = 'PedidoDaEtapaError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
const HTTP_UNPROCESSABLE = 422;

function falhaHttp(err: unknown): { status?: number | undefined; detalhe?: unknown } {
  if (!axios.isAxiosError(err)) return {};
  const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  return { status: err.response?.status, detalhe };
}

export async function lerEtapasDoCaso(
  client: AxiosInstance,
  caseId: string,
): Promise<EtapasDoCasoData> {
  try {
    const { data } = await client.get<EtapasDoCasoData>(`/casos/${caseId}/etapas`);
    return data;
  } catch (err) {
    const { status } = falhaHttp(err);
    throw new EtapasDoCasoError(status === HTTP_FORBIDDEN ? 'sem_permissao' : 'indisponivel');
  }
}

export async function pedirNaEtapa(
  client: AxiosInstance,
  caseId: string,
  pedido: PedidoDaEtapaData,
): Promise<PedidoDaEtapaRecordData> {
  try {
    const { data } = await client.post<PedidoDaEtapaRecordData>(
      `/casos/${caseId}/etapas/pedidos`,
      pedido,
    );
    return data;
  } catch (err) {
    const { status, detalhe } = falhaHttp(err);
    if (status === HTTP_CONFLICT && detalhe === 'PEDIDO_SEM_FALHA') {
      throw new PedidoDaEtapaError('sem_falha');
    }
    if (status === HTTP_UNPROCESSABLE && typeof detalhe !== 'string') {
      throw new PedidoDaEtapaError('justificativa');
    }
    throw new PedidoDaEtapaError('indisponivel');
  }
}

export type LiberacaoNaTela = LiberacaoDaEtapa | 'aguardando';

/** A liberação que a tela mostra: a da conferência de agora, ou aguardando. */
export function liberacaoNaTela(etapa: EtapaDoCasoData): LiberacaoNaTela {
  return etapa.agora?.liberacao ?? 'aguardando';
}

export interface ConsultaTravada {
  provedor: ProvedorExterno;
  /** O orçamento de tentativas acabou: a análise fica travada até a equipe agir. */
  esgotada: boolean;
}

const PROVEDORES: ReadonlySet<string> = new Set(['lemit', 'geocoder', 'ip_intel', 'mapa']); // branding:allow — chaves do contrato do servidor

/** As consultas do sistema que travam a análise, uma por provedor (esgotada vence). */
export function consultasTravadas(etapas: EtapasDoCasoData): ConsultaTravada[] {
  const analise = etapas.etapas.find((e) => e.etapa === 'analise')?.agora;
  const porProvedor = new Map<ProvedorExterno, boolean>();
  for (const c of analise?.criterios ?? []) {
    const provedor = c.parametros.provedor;
    if (c.chave !== 'consultas_do_sistema' || c.resultado !== 'falhou') continue;
    if (typeof provedor !== 'string' || !PROVEDORES.has(provedor)) continue;
    const chave = provedor as ProvedorExterno;
    porProvedor.set(chave, (porProvedor.get(chave) ?? false) || c.se_falhar === 'bloqueado');
  }
  return [...porProvedor].map(([provedor, esgotada]) => ({ provedor, esgotada }));
}

/** A ação principal da etapa que não liberou, ou que aguarda ser executada com a anterior aprovada:
 * a tela onde a equipe resolve. O recebimento, a extração e o restante da análise se resolvem na
 * própria pré-análise (a custódia, a conferência da leitura logo abaixo e o Verificar), e a condição
 * de saída diz como. */
export type AcaoDaEtapa = 'consulta' | 'mesa' | 'laudo' | 'certificacao' | null;

/** A etapa cuja aprovação libera a execução desta: o `DEPENDE_DE` do servidor (ADR-0101 §2.1). */
const DEPENDE_DE: Readonly<Record<EtapaDoFluxo, EtapaDoFluxo | null>> = {
  recebimento: null,
  extracao: null,
  analise: 'extracao',
  resultado: 'analise',
  revisao: 'analise',
  composicao: 'analise',
  assinatura: 'revisao',
  entrega: 'assinatura',
};

const ACAO_POR_ETAPA: Readonly<Record<EtapaDoFluxo, AcaoDaEtapa>> = {
  recebimento: null,
  extracao: null,
  analise: null,
  resultado: 'mesa',
  revisao: 'mesa',
  composicao: 'laudo',
  assinatura: 'certificacao',
  entrega: 'certificacao',
};

/** A etapa ainda não executada cuja anterior está aprovada: a vez dela (a primeira revisão, a
 * primeira composição). A que aguarda a anterior não tem ação (F-E147.F3-01). */
function aVezDela(etapa: EtapaDoCasoData, etapas: readonly EtapaDoCasoData[]): boolean {
  if (etapa.aguardando?.motivo !== 'nao_executada') return false;
  const anterior = DEPENDE_DE[etapa.etapa];
  if (anterior === null) return true;
  return etapas.find((e) => e.etapa === anterior)?.agora?.liberacao === 'aprovado';
}

export function acaoDaEtapa(
  etapa: EtapaDoCasoData,
  etapas: readonly EtapaDoCasoData[],
): AcaoDaEtapa {
  const agora = etapa.agora;
  if (agora === null) return aVezDela(etapa, etapas) ? ACAO_POR_ETAPA[etapa.etapa] : null;
  if (agora.liberacao === 'aprovado') return null;
  if (etapa.etapa === 'analise' && agora.motivo === 'consultas_do_sistema') return 'consulta';
  return ACAO_POR_ETAPA[etapa.etapa];
}

/** O critério que trava a etapa: crítico e falhou ou ficou inconclusivo. */
export function trava(c: CriterioConferidoData): boolean {
  return c.critico && (c.resultado === 'falhou' || c.resultado === 'inconclusivo');
}

/** O critério que dá a liberação da etapa que não liberou: do motivo, travando, e com a mesma
 * consequência da etapa (a consulta esgotada, e não a que ainda tem tentativa, explica o bloqueio). */
export function criterioQueTravou(agora: ConferenciaDaEtapaData): CriterioConferidoData | null {
  const doMotivo = agora.criterios.filter((c) => c.chave === agora.motivo && trava(c));
  return doMotivo.find((c) => c.se_falhar === agora.liberacao) ?? doMotivo[0] ?? null;
}

/** A primeira etapa que não liberou — onde a equipe começa. */
export function primeiraPendente(etapas: EtapasDoCasoData): EtapaDoCasoData | null {
  return (
    etapas.etapas.find(
      (e) => liberacaoNaTela(e) === 'corrigir' || liberacaoNaTela(e) === 'bloqueado',
    ) ?? null
  );
}

/** O espelho no caso trava a análise? `null`/sem a análise: caso sem a conferência das etapas, ou a
 * análise ainda não executou — os portões de sempre valem. */
export function analiseTravadaNoCaso(
  liberacoes: Partial<Record<string, LiberacaoNoCaso | string>> | null | undefined,
): boolean {
  const analise = liberacoes?.analise;
  return analise !== undefined && analise !== 'aprovado';
}
